import React, { useState, useEffect } from 'react';
import { useAPIKeys } from '../contexts/APIKeyContext';

const APIKeySetup = ({ onClose, isModal = true }) => {
  const {
    providers,
    updateProvider,
    testProvider,
    ollamaAvailable,
    ollamaModels,
    checkOllama,
    clearAllKeys
  } = useAPIKeys();

  const [activeTab, setActiveTab] = useState('ollama');
  const [showKeys, setShowKeys] = useState({});
  const [testResults, setTestResults] = useState({});
  const [testing, setTesting] = useState({});

  // Provider configuration
  const providerTabs = [
    { key: 'ollama', name: 'Ollama', icon: '🦙', description: 'Free local AI - run models on your own hardware', category: 'local' },
    { key: 'groq', name: 'Groq', icon: '⚡', description: 'Ultra-fast inference with Llama models', category: 'fast' },
    { key: 'deepseek', name: 'DeepSeek', icon: '🔮', description: 'Low cost, high quality Chinese AI', category: 'budget' },
    { key: 'together', name: 'Together', icon: '🤝', description: 'Many open-source models available', category: 'budget' },
    { key: 'mistral', name: 'Mistral', icon: '🌬️', description: 'European AI, excellent quality', category: 'quality' },
    { key: 'openrouter', name: 'OpenRouter', icon: '🔀', description: 'Access 100+ models through one API', category: 'multi' },
    { key: 'cohere', name: 'Cohere', icon: '🧠', description: 'Enterprise-grade AI with RAG support', category: 'enterprise' },
    { key: 'openai', name: 'OpenAI', icon: '🤖', description: 'GPT-4o and GPT-4o-mini models', category: 'premium' },
    { key: 'anthropic', name: 'Claude', icon: '🎭', description: 'Anthropic Claude - excellent reasoning', category: 'premium' },
    { key: 'gemini', name: 'Gemini', icon: '✨', description: 'Google AI with multimodal support', category: 'premium' },
    { key: 'custom', name: 'Custom', icon: '🔧', description: 'Any OpenAI-compatible API endpoint', category: 'other' },
  ];

  // Check Ollama availability on mount
  useEffect(() => {
    if (providers.ollama?.endpoint) {
      checkOllama(providers.ollama.endpoint);
    }
  }, []);

  const handleTestConnection = async (providerKey) => {
    setTesting(prev => ({ ...prev, [providerKey]: true }));
    setTestResults(prev => ({ ...prev, [providerKey]: null }));

    const result = await testProvider(providerKey);
    setTestResults(prev => ({ ...prev, [providerKey]: result }));
    setTesting(prev => ({ ...prev, [providerKey]: false }));
  };

  const toggleShowKey = (providerKey) => {
    setShowKeys(prev => ({ ...prev, [providerKey]: !prev[providerKey] }));
  };

  const renderProviderForm = (providerKey) => {
    const provider = providers[providerKey];
    const testResult = testResults[providerKey];
    const isTesting = testing[providerKey];

    if (providerKey === 'ollama') {
      return (
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">🦙</span>
              <div>
                <h4 className="font-medium text-blue-800 dark:text-blue-300">Ollama - Free Local AI</h4>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  Run AI models locally on your computer for free. No API key required.
                </p>
              </div>
            </div>
          </div>

          {ollamaAvailable && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-400 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Ollama detected! {ollamaModels.length} model(s) available.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ollama Endpoint
            </label>
            <input
              type="text"
              value={provider.endpoint}
              onChange={(e) => updateProvider('ollama', { endpoint: e.target.value })}
              placeholder="http://localhost:11434"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Default: http://localhost:11434 (local) or your remote Ollama server URL
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Model
            </label>
            {ollamaModels.length > 0 ? (
              <select
                value={provider.model}
                onChange={(e) => updateProvider('ollama', { model: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                {ollamaModels.map(model => (
                  <option key={model} value={model}>{model}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                value={provider.model}
                onChange={(e) => updateProvider('ollama', { model: e.target.value })}
                placeholder="llama2, mistral, codellama..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={provider.enabled}
                onChange={(e) => updateProvider('ollama', { enabled: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <span className="text-gray-700 dark:text-gray-300">Enable Ollama</span>
            </label>

            <button
              onClick={() => handleTestConnection('ollama')}
              disabled={isTesting}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 flex items-center space-x-2"
            >
              {isTesting ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Testing...</span>
                </>
              ) : (
                <span>Test Connection</span>
              )}
            </button>
          </div>
        </div>
      );
    }

    // Model options by provider
    const modelOptions = {
      openai: [
        'gpt-4o',
        'gpt-4o-mini',
        'gpt-4-turbo',
        'gpt-4-turbo-preview',
        'gpt-4',
        'gpt-3.5-turbo',
        'gpt-3.5-turbo-16k',
        'o1-preview',
        'o1-mini'
      ],
      anthropic: [
        'claude-3-5-sonnet-20241022',
        'claude-3-5-haiku-20241022',
        'claude-3-opus-20240229',
        'claude-3-sonnet-20240229',
        'claude-3-haiku-20240307',
        'claude-2.1',
        'claude-2.0',
        'claude-instant-1.2'
      ],
      groq: [
        'llama-3.3-70b-versatile',
        'llama-3.3-70b-specdec',
        'llama-3.1-70b-versatile',
        'llama-3.1-8b-instant',
        'llama-3.2-90b-vision-preview',
        'llama-3.2-11b-vision-preview',
        'llama-3.2-3b-preview',
        'llama-3.2-1b-preview',
        'mixtral-8x7b-32768',
        'gemma2-9b-it',
        'gemma-7b-it'
      ],
      mistral: [
        'mistral-large-latest',
        'mistral-large-2411',
        'mistral-medium-latest',
        'mistral-small-latest',
        'mistral-small-2409',
        'codestral-latest',
        'codestral-2405',
        'open-mistral-nemo',
        'open-mixtral-8x22b',
        'open-mixtral-8x7b',
        'open-mistral-7b',
        'pixtral-12b-2409',
        'pixtral-large-latest'
      ],
      together: [
        'meta-llama/Meta-Llama-3.1-405B-Instruct-Turbo',
        'meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo',
        'meta-llama/Meta-Llama-3.1-8B-Instruct-Turbo',
        'meta-llama/Llama-3-70b-chat-hf',
        'meta-llama/Llama-3-8b-chat-hf',
        'mistralai/Mixtral-8x22B-Instruct-v0.1',
        'mistralai/Mixtral-8x7B-Instruct-v0.1',
        'Qwen/Qwen2.5-72B-Instruct-Turbo',
        'Qwen/Qwen2-72B-Instruct',
        'deepseek-ai/deepseek-llm-67b-chat',
        'google/gemma-2-27b-it',
        'google/gemma-2-9b-it',
        'NousResearch/Nous-Hermes-2-Mixtral-8x7B-DPO',
        'Gryphe/MythoMax-L2-13b'
      ],
      openrouter: [
        'anthropic/claude-3.5-sonnet',
        'anthropic/claude-3-opus',
        'openai/gpt-4o',
        'openai/gpt-4-turbo',
        'openai/o1-preview',
        'google/gemini-pro-1.5',
        'google/gemini-flash-1.5',
        'meta-llama/llama-3.1-405b-instruct',
        'meta-llama/llama-3.1-70b-instruct',
        'meta-llama/llama-3-8b-instruct',
        'mistralai/mistral-large',
        'mistralai/mistral-medium',
        'mistralai/mistral-7b-instruct',
        'cohere/command-r-plus',
        'deepseek/deepseek-chat',
        'deepseek/deepseek-coder',
        'perplexity/llama-3.1-sonar-large-128k-online',
        'qwen/qwen-2-72b-instruct'
      ],
      cohere: [
        'command-r-plus-08-2024',
        'command-r-plus',
        'command-r-08-2024',
        'command-r',
        'command',
        'command-light',
        'command-nightly'
      ],
      deepseek: [
        'deepseek-chat',
        'deepseek-coder',
        'deepseek-reasoner'
      ],
      gemini: [
        'gemini-2.0-flash-exp',
        'gemini-1.5-pro-latest',
        'gemini-1.5-pro',
        'gemini-1.5-flash-latest',
        'gemini-1.5-flash',
        'gemini-1.5-flash-8b',
        'gemini-pro',
        'gemini-pro-vision'
      ],
      ollama: [
        'llama3.2',
        'llama3.1',
        'llama3',
        'llama2',
        'mistral',
        'mixtral',
        'qwen2.5',
        'qwen2',
        'deepseek-coder-v2',
        'codellama',
        'phi3',
        'gemma2',
        'gemma',
        'neural-chat',
        'starling-lm'
      ]
    };

    // Get API key docs URLs
    const apiKeyUrls = {
      openai: 'https://platform.openai.com/api-keys',
      anthropic: 'https://console.anthropic.com/settings/keys',
      groq: 'https://console.groq.com/keys',
      mistral: 'https://console.mistral.ai/api-keys',
      together: 'https://api.together.xyz/settings/api-keys',
      openrouter: 'https://openrouter.ai/keys',
      cohere: 'https://dashboard.cohere.com/api-keys',
      deepseek: 'https://platform.deepseek.com/api_keys',
      gemini: 'https://aistudio.google.com/app/apikey'
    };

    // For API-based providers
    return (
      <div className="space-y-4">
        {/* API Key URL hint */}
        {apiKeyUrls[providerKey] && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Get your API key at:{' '}
            <a
              href={apiKeyUrls[providerKey]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              {apiKeyUrls[providerKey].replace('https://', '')}
            </a>
          </div>
        )}

        {providerKey === 'custom' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              API Endpoint
            </label>
            <input
              type="text"
              value={provider.endpoint}
              onChange={(e) => updateProvider(providerKey, { endpoint: e.target.value })}
              placeholder="https://api.example.com/v1/chat/completions"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            API Key
          </label>
          <div className="relative">
            <input
              type={showKeys[providerKey] ? 'text' : 'password'}
              value={provider.apiKey}
              onChange={(e) => updateProvider(providerKey, { apiKey: e.target.value })}
              placeholder={`Enter your ${provider.name} API key`}
              className="w-full px-4 py-2 pr-12 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={() => toggleShowKey(providerKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              {showKeys[providerKey] ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Model selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Model
          </label>
          {modelOptions[providerKey] ? (
            <select
              value={provider.model}
              onChange={(e) => updateProvider(providerKey, { model: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {modelOptions[providerKey].map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          ) : (
            <input
              type="text"
              value={provider.model}
              onChange={(e) => updateProvider(providerKey, { model: e.target.value })}
              placeholder="Model name"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          )}
        </div>

        <div className="flex items-center justify-between pt-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={provider.enabled}
              onChange={(e) => updateProvider(providerKey, { enabled: e.target.checked })}
              className="w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-gray-700 dark:text-gray-300">Enable {provider.name}</span>
          </label>

          <button
            onClick={() => handleTestConnection(providerKey)}
            disabled={isTesting || (!provider.apiKey && providerKey !== 'ollama')}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 flex items-center space-x-2"
          >
            {isTesting ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span>Testing...</span>
              </>
            ) : (
              <span>Test Connection</span>
            )}
          </button>
        </div>
      </div>
    );
  };

  const content = (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Configure AI Providers
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Set up your API keys or use local Ollama
            </p>
          </div>
          {isModal && onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex overflow-x-auto">
          {providerTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
              {providers[tab.key]?.enabled && (
                <span className="ml-2 w-2 h-2 bg-green-500 rounded-full inline-block"></span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Provider description */}
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {providerTabs.find(t => t.key === activeTab)?.description}
        </p>

        {/* Provider form */}
        {renderProviderForm(activeTab)}

        {/* Test result */}
        {testResults[activeTab] && (
          <div className={`mt-4 p-3 rounded-lg ${
            testResults[activeTab].success
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
          }`}>
            <p className={`text-sm flex items-center ${
              testResults[activeTab].success
                ? 'text-green-700 dark:text-green-400'
                : 'text-red-700 dark:text-red-400'
            }`}>
              {testResults[activeTab].success ? (
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              {testResults[activeTab].message || testResults[activeTab].error}
            </p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              if (window.confirm('Clear all API keys and settings?')) {
                clearAllKeys();
              }
            }}
            className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
          >
            Clear All Keys
          </button>

          {isModal && onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 bg-primary hover:bg-primary-dark text-white font-medium rounded-lg transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        {content}
      </div>
    );
  }

  return content;
};

export default APIKeySetup;
